import { featureKeys as assetFeatureKeys } from '@/api/equipment/assets';
import { useCompleteInspection } from '@/api/equipment/inspection-logs/completeInspection';
import { useDeleteInspectionLog } from '@/api/equipment/inspection-logs/deleteInspectionLog';
import { useInspectionLog } from '@/api/equipment/inspection-logs/getInspectionLog';
import { useUpdateInspectionLog } from '@/api/equipment/inspection-logs/updateInspectionLog';
import { featureKeys as inspectionFeatureKeys } from '@/api/equipment/inspections';
import { useInspection } from '@/api/equipment/inspections/getInspection';
import { featureKeys as vehicleFeatureKeys } from '@/api/equipment/vehicles';
import { ConfirmationDialog } from '@/components/Elements';
import { Button } from '@/components/ui/elements/button';
import { Input } from '@/components/ui/elements/input';
import { Heading1, Heading2 } from '@/components/ui/typography/Typography';
import { PolicyGate } from '@/features/auth/authorization';
import queryClient from '@/lib/react-query';
import { formatDate } from '@/utils/format';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from '@tanstack/react-router';
import { InspectionLogForm } from './InspectionLogForm';
import { InspectionStep } from './results/InspectionStep';

export const InspectionReport = () => {
  const { inspection_id: inspectionId, id: inspectionLogId } = useParams({
    strict: false,
  });
  const navigate = useNavigate();

  const { data: inspection } = useInspection({
    id: inspectionId!,
    config: { enabled: !!inspectionId },
  });
  const { data: inspectionLog } = useInspectionLog({
    inspection,
    id: inspectionLogId!,
    config: {
      enabled: !!inspectionLogId && !!inspection,
    },
  });

  const { mutate: updateInspectionLog } = useUpdateInspectionLog();
  const { mutate: deleteLog } = useDeleteInspectionLog();
  const { mutate: completeInspectionLog } = useCompleteInspection();

  const handleClose = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    navigate({ to: '/equipment/dashboard' });
  };

  const handleUpdate = (values: { usage_reading: number | null }) => {
    if (!inspection || !inspectionLog) return;

    updateInspectionLog(
      {
        inspection,
        id: inspectionLog.id,
        data: {
          usage_reading: values.usage_reading,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: vehicleFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: assetFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: inspectionFeatureKeys.all });
        },
      },
    );
  };

  const handleDelete = () => {
    if (!inspection || !inspectionLog) return;

    deleteLog(
      {
        inspection,
        id: inspectionLog.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: vehicleFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: assetFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: inspectionFeatureKeys.all });
          navigate({
            to: `/equipment/dashboard`,
          });
        },
      },
    );
  };

  const handleCompleteInspection = () => {
    if (!inspection || !inspectionLog) return;

    completeInspectionLog(
      {
        inspection,
        log: inspectionLog,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: vehicleFeatureKeys.all });
          queryClient.invalidateQueries({ queryKey: assetFeatureKeys.all });
          navigate({ to: `/equipment/dashboard` });
        },
      },
    );
  };

  if (!inspection) return <>Loading...</>;

  // Prepare the inspection steps. First get the master list from the inspection
  // Then map over them to see if there is a log entry for each step in the current inspection log
  // This way we can show all steps, even if they haven't been logged yet
  const inspectionSteps =
    inspection.steps.map((step) => {
      const resultEntry = inspectionLog?.results?.find(
        (result) => result.inspection_step_id === step.inspection_step_id,
      );
      return {
        ...step,
        result: resultEntry,
      };
    }) || [];

  return (
    <div className="w-full flex flex-col items-start">
      <div className="flex flex-row w-full items-center justify-between p-4 border-b">
        {inspectionLog?.is_complete && inspectionLog.completed_at ? (
          <Heading1>
            {inspectionLog.inspection.name} - {formatDate(inspectionLog.completed_at)}
          </Heading1>
        ) : (
          <Heading1>{inspectionLog ? 'Continue' : 'New'} Inspection Report</Heading1>
        )}

        <Button type="button" variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </div>
      <div className="flex flex-col md:flex-row w-full justify-between">
        <div className="w-full md:w-1/3 flex flex-col items-start md:border-r p-2 md:p-6">
          {inspectionLog?.is_complete ? (
            <div className="mb-4 p-4 text-green-700 bg-green-100 border border-green-200 rounded w-full">
              <Heading2>{inspectionLog.name}</Heading2>
              <p className="text-green-700 text-sm">
                This inspection report was completed on{' '}
                {inspectionLog.completed_at && formatDate(inspectionLog.completed_at, 'MM/dd/yyyy')}
                .
              </p>
              <p className="">{inspectionLog.notes}</p>
            </div>
          ) : (
            <InspectionLogForm inspection={inspection} inspectionLog={inspectionLog} />
          )}

          {inspectionLog && (
            <PolicyGate policy="equipment.maintenance.inspections.delete" meta={{ inspectionLog }}>
              <span className="ml-6 mr-12 w-full ">
                <ConfirmationDialog
                  triggerButton={<Button variant="destructive">Delete Inspection Report</Button>}
                  confirmButtonText="Remove"
                  confirmAction={handleDelete}
                  title={`Delete this entry for ${inspectionLog?.name}?`}
                  body="Are you sure you want to delete this entry? This action cannot be undone."
                />
              </span>
            </PolicyGate>
          )}
          {inspectionLog && inspectionLog.attachments && (
            <div className="pl-3 py-3">
              {inspectionLog.attachments.map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.download_link}
                  rel="noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  <DocumentArrowDownIcon className="h-5 w-5" />
                </a>
              ))}
            </div>
          )}
        </div>

        {inspectionLog && (
          <div className="w-full md:w-2/3 flex flex-col items-start p-2 md:p-4">
            <div className="w-full">
              {inspectionSteps.map((step) => (
                <InspectionStep key={`step-${step.id}`} step={step} inspectionLog={inspectionLog} />
              ))}
            </div>
            <div className="my-4 w-full" />
            {!inspectionLog?.is_complete && (
              <>
                {inspectionLog.inspection.maintainable_type === 'vehicle' && (
                  <div>
                    <label className="block mb-2 font-medium">Current Mileage</label>
                    <Input
                      placeholder="Current Mileage"
                      defaultValue={inspectionLog.usage_reading ?? ''}
                      type="number"
                      onBlur={(event) =>
                        handleUpdate({ usage_reading: Number(event.target.value) || null })
                      }
                    />
                  </div>
                )}
                <div className="p-6 mb-8 w-full">
                  <Button
                    type="button"
                    size="lg"
                    onClick={handleCompleteInspection}
                    disabled={!inspectionLog?.can_be_completed}
                    className={
                      !inspectionLog?.can_be_completed
                        ? 'bg-zinc-400 cursor-not-allowed hover:bg-zinc-400 border inline-block w-full '
                        : 'w-full'
                    }
                  >
                    Complete Inspection
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
